<template>
  <div class="full-screen-background">
    <v-container fluid>
      <v-row>
        <!-- Coluna da escala -->
        <v-col cols="12" md="9">
          <div class="escala-wrapper text-center">
            <h1 class="text-h3 font-weight-bold my-4">
              {{ t('escala.title') }}
              <v-btn
                v-if="extraEligible"
                color="orange darken-2"
                class="ml-4"
                :loading="isLoading"
                :disabled="isLoading"
                @click="handleExtraRedeem"
              >
                {{ t('escala.btnExtraEligible') }}
              </v-btn>
            </h1>

            <h2 class="text-subtitle-1 mb-2">
              {{ t('escala.subtitle', { currentMonthName, timeRemaining }) }}
            </h2>

            <div class="spent mb-4">
              {{ t('escala.spent', { spentPoints }) }}
            </div>

            <v-divider></v-divider>

            <v-row class="tiers-row mt-4" justify="center">
              <v-col
                v-for="(tier, idx) in tiers"
                :key="idx"
                cols="12"
                sm="4"
              >
                <v-card
                  :class="['tier-card',{ reached: isTierReached(idx), redeemed: isTierRedeemed(idx) }]"
                  outlined
                  @click="selectTier(idx)"
                >
                  <v-card-text class="text-center">
                    <div class="tier-header">
                      <span class="tier-title">{{ idx+1 }} – {{ tier.name }}</span>
                      <v-icon v-if="isTierRedeemed(idx)" class="ml-2">mdi-check-circle</v-icon>
                    </div>
                    <v-img
                      v-if="tier.image"
                      :src="tier.image"
                      class="tier-image my-2"
                      contain
                    />
                    <div class="tier-value">{{ tier.threshold }} pontos</div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>

            <v-row v-if="selectedTierIndex !== null" class="justify-center mt-6">
              <v-col cols="12" sm="8">
                <v-card outlined>
                  <v-card-title>
                    {{ selectedTierIndex+1 }} – {{ tiers[selectedTierIndex].name }}
                  </v-card-title>
                  <v-card-text>
                    <div class="grid-title mb-4">{{ t('escala.chooseYourRescue') }}</div>
                    <v-row>
                      <v-col
                        v-for="(item, idx2) in getDisplayItems(selectedTierIndex)"
                        :key="selectedTierIndex+'-'+idx2"
                        cols="4"
                        class="d-flex"
                      >
                        <v-card class="item-card" outlined>
                          <template v-if="item.bundle">
                            <div class="card-name mb-2 text-left">
                              <strong>{{ t('escala.bundlePackage') }}</strong>
                              <ul class="bundle-list mb-0">
                                <li v-for="sub in item.items" :key="sub.id">
                                  <v-img
                                    v-if="sub.image"
                                    :src="sub.image"
                                    class="inline-icon mr-1"
                                    contain
                                    width="30" height="30"
                                  />
                                  {{ sub.name }} ×{{ sub.quantity }}
                                </li>
                              </ul>
                            </div>
                          </template>
                          <template v-else>
                            <v-img
                              v-if="item.image"
                              :src="item.image"
                              class="item-image my-2"
                              contain
                            />
                            <div class="card-name mb-2 text-left">
                              {{ item.name }} ×{{ item.quantity }}
                            </div>
                          </template>
                          <v-btn
                            small
                            color="red"
                            dark
                            :loading="isLoading"
                            :disabled="
                              isLoading ||
                              !isTierReached(selectedTierIndex) ||
                              isTierRedeemed(selectedTierIndex) ||
                              isItemRedeemed(
                                selectedTierIndex,
                                item.bundle ? 'bundle' : idx2
                              )
                            "
                            @click="item.id===null
                              ? openSurpriseModal(selectedTierIndex)
                              : resgatarItem(selectedTierIndex, idx2, item.bundle)"
                          >
                            {{
                              isItemRedeemed(
                                selectedTierIndex,
                                item.bundle ? 'bundle' : idx2
                              )
                                ? t('escala.redeemed')
                                : t('escala.redeem')
                            }}
                          </v-btn>
                        </v-card>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </div>
        </v-col>

        <!-- Ranking e prêmios extras -->
        <v-col cols="12" md="3">
          <div class="ranking-wrapper">
            <h3 class="text-h6 font-weight-medium mb-3 text-center">
              {{ t('escala.topDonorsTitle') }}
            </h3>
            <v-simple-table dense>
              <thead><tr>
                <th style="width:24px;"></th>
                <th style="width:24px;">#</th>
                <th>{{ t('escala.columnPlayer') }}</th>
                <th style="text-align:right;">{{ t('escala.columnSpent') }}</th>
              </tr></thead>
              <tbody>
                <tr v-for="(player, idx) in rankList" :key="player.account_name">
                  <td>
                    <v-icon v-if="idx===0" color="amber darken-2">mdi-medal</v-icon>
                    <v-icon v-else-if="idx===1" color="grey lighten-1">mdi-medal</v-icon>
                    <v-icon v-else-if="idx===2" color="brown lighten-1">mdi-medal</v-icon>
                  </td>
                  <td>{{ idx+1 }}</td>
                  <td>{{ player.nickname }}</td>
                  <td style="text-align:right;">{{ player.spent }} {{ t('escala.columnSpent') }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </div>
          <div class="recompensar-wrapper">
            <h3 class="text-h6 font-weight-medium mb-3 text-center">
              {{ t('escala.extraRewardsTitle') }}
            </h3>
            <!-- Top 1,2,3 extras -->
            <div class="extra-item mb-4" v-if="rankList[0]">
              <div class="d-flex align-center">
                <v-icon color="amber darken-2">mdi-medal</v-icon>
                <span class="ml-2">({{ rankList[0].nickname }})</span>
                <div class="ml-4 d-flex align-center">
                  <v-img src="/auxiliadores/inter.png" width="32" height="32" contain/>
                  <span class="ml-1">{{ t('escala.surpriseGMClick') }}</span>
                </div>
                <div class="ml-3 d-flex align-center">
                  <v-img
                    :src="tiers[7].image"
                    class="reward-image"
                    width="32" height="32"
                    contain
                  />
                  <span class="ml-1">
                    {{ t('escala.clayX',{quantity:tiers[7].items[0].quantity}) }}
                  </span>
                </div>
              </div>
            </div>
            <v-divider thickness="10"></v-divider>
            <div class="extra-item mb-4" v-if="rankList[1]">
              <div class="d-flex align-center">
                <v-icon color="grey lighten-1">mdi-medal</v-icon>
                <span class="ml-2">({{ rankList[1].nickname }})</span>
                <div class="ml-4 d-flex align-center">
                  <v-img src="/auxiliadores/inter.png" width="32" height="32" contain/>
                  <span class="ml-1">{{ t('escala.surpriseGMClick') }}</span>
                </div>
              </div>
            </div>
            <v-divider thickness="10"></v-divider>
            <div class="extra-item mb-4" v-if="rankList[2]">
              <div class="d-flex align-center">
                <v-icon color="brown lighten-1">mdi-medal</v-icon>
                <span class="ml-2">({{ rankList[2].nickname }})</span>
                <div class="ml-4 d-flex align-center">
                  <v-img
                    :src="tiers[7].image"
                    class="reward-image"
                    width="32" height="32"
                    contain
                  />
                  <span class="ml-1">
                    {{ t('escala.clayX',{quantity:tiers[7].items[0].quantity}) }}
                  </span>
                </div>
              </div>
            </div>
            <div class="instruction">
              {{ t('escala.instruction') }}
            </div>
          </div>
        </v-col>
      </v-row>

      <!-- Dialog Surpresa GM -->
      <v-dialog v-model="surpriseDialog" max-width="500">
        <v-card>
          <v-card-title>{{ t('escala.chooseYourSurprise') }}</v-card-title>
          <v-card-text>
            <v-radio-group v-model="selectedSurpriseOption">
              <v-row>
                <v-col
                  v-for="id in surpriseOptions" :key="id"
                  cols="12" sm="4" class="d-flex flex-column align-center"
                >
                  <v-radio :label="nameMap[id]||t('escala.noDescription')" :value="id"/>
                  <v-img
                    v-if="textureMap[id]"
                    :src="`/auxiliadores/itemicon/${textureMap[id]}.png`"
                    width="50" height="50" contain class="my-2"
                  />
                </v-col>
              </v-row>
            </v-radio-group>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <v-spacer/>
            <v-btn text @click="cancelSurprise" :disabled="isLoading">{{ t('escala.cancel') }}</v-btn>
            <v-btn
              color="red" dark
              :loading="isLoading"
              :disabled="isLoading||!selectedSurpriseOption"
              @click="confirmSurpriseRescue"
            >
              {{ t('escala.redeemSurprise') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Dialog Prêmio Extra -->
      <v-dialog v-model="extraDialog" max-width="400">
        <v-card>
          <v-card-title>{{ t('escala.rescueExtraTitle') }}</v-card-title>
          <v-card-text>
            <div v-if="extraRank===1">
              <p><strong>{{ t('escala.youAre',{rank:1}) }}</strong></p>
              <p>{{ t('escala.availablePrizes') }}</p>
              <ul>
                <li>
                  <v-img
                    :src="tiers[7].image"
                    class="inline-icon mr-1"
                    width="30" height="30" contain
                  />
                  {{ t('escala.clayX',{quantity:tiers[7].items[0].quantity}) }}
                </li>
                <li>
                  <v-img src="/auxiliadores/inter.png" class="inline-icon mr-1" width="30" height="30" contain/>
                  {{ t('escala.surpriseGMClick') }}
                </li>
              </ul>
            </div>
            <div v-else-if="extraRank===2">
              <p><strong>{{ t('escala.youAre',{rank:2}) }}</strong></p>
              <p>{{ t('escala.availablePrize') }}</p>
              <ul>
                <li>
                  <v-img src="/auxiliadores/inter.png" class="inline-icon mr-1" width="30" height="30" contain/>
                  {{ t('escala.surpriseGMClick') }}
                </li>
              </ul>
            </div>
            <div v-else-if="extraRank===3">
              <p><strong>{{ t('escala.youAre',{rank:3}) }}</strong></p>
              <p>{{ t('escala.availablePrize') }}</p>
              <ul>
                <li>
                  <v-img
                    :src="tiers[7].image"
                    class="inline-icon mr-1"
                    width="30" height="30" contain
                  />
                  {{ t('escala.clayX',{quantity:tiers[7].items[0].quantity}) }}
                </li>
              </ul>
            </div>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <v-spacer/>
            <v-btn text @click="extraDialog=false" :disabled="isLoading">{{ t('escala.cancel') }}</v-btn>
            <v-btn
              color="green darken-1" dark
              :loading="isLoading"
              :disabled="isLoading"
              @click="confirmExtraRescue"
            >
              {{ t('escala.redeem') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Dialog Surpresa GM Extra -->
      <v-dialog v-model="extraSurpriseDialog" max-width="500">
        <v-card>
          <v-card-title>{{ t('escala.chooseYourExtraSurprise') }}</v-card-title>
          <v-card-text>
            <v-radio-group v-model="selectedSurpriseOption">
              <v-row>
                <v-col
                  v-for="id in surpriseOptions"
                  :key="id"
                  cols="12" sm="4"
                  class="d-flex flex-column align-center"
                >
                  <v-radio :label="nameMap[id]||t('escala.noDescription')" :value="id"/>
                  <v-img
                    v-if="textureMap[id]"
                    :src="`/auxiliadores/itemicon/${textureMap[id]}.png`"
                    width="50" height="50" contain class="my-2"
                  />
                </v-col>
              </v-row>
            </v-radio-group>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <v-spacer/>
            <v-btn text @click="cancelExtraSurprise" :disabled="isLoading">{{ t('escala.cancel') }}</v-btn>
            <v-btn
              color="red" dark
              :loading="isLoading"
              :disabled="isLoading||!selectedSurpriseOption"
              @click="confirmExtraSurpriseChoice"
            >
              {{ t('escala.redeemSurprise') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import { useI18n } from 'vue-i18n'
import * as THREE from 'three'
import {
  fetchScaleSpentPoints,
  fetchScaleRescueStatus,
  fetchScaleRanking,
  redeemScale,
  redeemScaleItem,
  fetchExtraStatus,
  redeemExtraReward,
  redeemExtraSurprise
} from '@/services/api.js'
import { auth } from '@/auth'

export default defineComponent({
  name: 'EscalaPrêmios',
  setup() {
    const { t } = useI18n()
    return { t }
  },
  data() {
    return {
      tiers: [
        { name: 'Bronze', threshold: 2500, imageItemId: 49323, items: [{ id: 49323, quantity: 50 },{ id:52429,quantity:1 }], bundle:false },
        { name:'Prata',threshold:5000,imageItemId:43665,items:[
            {id:43665,quantity:30},{id:43659,quantity:30},{id:42208,quantity:30},
            {id:43663,quantity:30},{id:43661,quantity:30},{id:49000,quantity:30}
          ],bundle:true
        },
        { name:'Ouro',threshold:7500,imageItemId:51299,items:[
            {id:51299,quantity:1},{id:46824,quantity:1},{id:55172,quantity:1},{id:52235,quantity:1}
          ],bundle:true
        },
        { name:'Platina',threshold:10000,imageItemId:55912,items:[
            {id:55912,quantity:1},{id:55911,quantity:1},{id:55910,quantity:1},{id:55909,quantity:1},{id:55908,quantity:1}
          ],bundle:false
        },
        { name:'Diamante',threshold:15000,imageItemId:49323,items:[{id:49323,quantity:150}],bundle:false },
        { name:'Mestre',threshold:20000,imageItemId:45527,items:[
            {id:50037,quantity:1},{id:53641,quantity:1},{id:45527,quantity:1},{id:53653,quantity:1}
          ],bundle:true
        },
        { name:'Lendário',threshold:25000,imageItemId:40362,items:[{id:40362,quantity:150}],bundle:false },
        { name:'Supremo',threshold:30000,imageItemId:40362,items:[{id:40362,quantity:250}],bundle:false }
      ],
      textureMap:{}, nameMap:{},
      selectedTierIndex:null,
      timeRemaining:'', timer:null,
      spentPoints:0,
      redeemedTiers:[],
      redeemedItems:{},
      rankList:[],
      surpriseDialog:false,
      surpriseTier:null,
      surpriseOptions:[52429,43665,55912],
      selectedSurpriseOption:null,
      extraEligible:false,
      extraRank:null,
      extraDialog:false,
      extraSurpriseDialog:false,
      isLoading:false
    }
  },
  computed:{
    currentMonthName(){
      return new Date().toLocaleString('pt-BR',{month:'long'})
    }
  },
  methods:{
    isTierReached(idx){ return this.tiers[idx].threshold<=this.spentPoints },
    isTierRedeemed(idx){ return this.redeemedTiers.includes(idx+1) },
    isItemRedeemed(scaleIdx,itemKey){
      const arr=this.redeemedItems[scaleIdx]||[]
      return arr.includes(itemKey)
    },
    selectTier(idx){ this.selectedTierIndex=idx },
    getDisplayItems(idx){
      if(idx===7){
        const sup=this.tiers[7]
        const its=sup.items.map(i=>({
          id:i.id,quantity:i.quantity,
          name:this.nameMap[i.id]||this.t('escala.noDescription'),
          image:this.textureMap[i.id]?`/auxiliadores/itemicon/${this.textureMap[i.id]}.png`:null
        }))
        return [...its,{id:null,quantity:1,name:this.t('escala.surpriseGMClick'),image:'/auxiliadores/inter.png',bundle:false}]
      }
      const t=this.tiers[idx]
      if(t.bundle){
        return [{bundle:true,items:t.items.map(i=>({
          id:i.id,quantity:i.quantity,
          name:this.nameMap[i.id]||this.t('escala.noDescription'),
          image:this.textureMap[i.id]?`/auxiliadores/itemicon/${this.textureMap[i.id]}.png`:null
        }))}]
      }
      return t.items.map(i=>({
        ...i,
        name:this.nameMap[i.id]||this.t('escala.noDescription'),
        image:this.textureMap[i.id]?`/auxiliadores/itemicon/${this.textureMap[i.id]}.png`:null
      }))
    },

    async resgatarItem(scaleIdx,itemIdx,bundle){
      if(this.isLoading)return
      this.isLoading=true
      try{
        await redeemScale(auth.token,scaleIdx+1)
        if(bundle){
          for(const it of this.tiers[scaleIdx].items){
            await redeemScaleItem(auth.token,scaleIdx+1,it.id,it.quantity)
          }
        } else {
          const sel=this.getDisplayItems(scaleIdx)[itemIdx]
          await redeemScaleItem(auth.token,scaleIdx+1,sel.id,sel.quantity)
        }
        const num=scaleIdx+1
        if(!this.redeemedTiers.includes(num))this.redeemedTiers.push(num)
        const key=bundle?'bundle':itemIdx
        const arr=this.redeemedItems[scaleIdx]||[]
        arr.push(key)
        this.redeemedItems={...this.redeemedItems,[scaleIdx]:arr}
        alert(this.t('escala.redeemed')+` ${num}!`)
      }catch(err){
        console.error('Erro ao resgatar item:',err)
        alert('Resgatar falhou. Tente novamente.')
      }finally{
        this.isLoading=false
      }
    },

    openSurpriseModal(idx){
      this.surpriseTier=idx
      this.selectedSurpriseOption=null
      this.surpriseDialog=true
    },
    cancelSurprise(){
      this.surpriseDialog=false
      this.surpriseTier=null
      this.selectedSurpriseOption=null
    },
    async confirmSurpriseRescue(){
      if(this.isLoading)return
      this.isLoading=true
      try{
        const num=this.surpriseTier+1
        await redeemScale(auth.token,num)
        await redeemScaleItem(auth.token,num,this.selectedSurpriseOption,1)
        if(!this.redeemedTiers.includes(num))this.redeemedTiers.push(num)
        const key=this.getDisplayItems(this.surpriseTier).findIndex(i=>i.id===null)
        const arr=this.redeemedItems[this.surpriseTier]||[]
        arr.push(key)
        this.redeemedItems={...this.redeemedItems,[this.surpriseTier]:arr}
        alert(this.t('escala.surpriseGMClick')+` (item ${this.selectedSurpriseOption})!`)
      }catch(err){
        console.error('Erro ao resgatar Surpresa GM:',err)
        alert('Resgatar falhou. Tente novamente.')
      }finally{
        this.isLoading=false
        this.cancelSurprise()
      }
    },

    handleExtraRedeem(){ this.extraDialog=true },

    async confirmExtraRescue(){
      if(this.isLoading)return
      this.isLoading=true
      try{
        if(this.extraRank===1){
          await redeemExtraReward(auth.token)
          this.extraDialog=false
          this.openExtraSurpriseModal()
        }else if(this.extraRank===2){
          this.extraDialog=false
          this.openExtraSurpriseModal()
        }else if(this.extraRank===3){
          const resp=await redeemExtraReward(auth.token)
          alert(this.t('escala.clayX',{quantity:resp.insertedClay.quantity})+` (ID ${resp.insertedClay.id})`)
          this.extraEligible=false
          this.extraDialog=false
        }
      }catch(err){
        console.error('Erro ao resgatar extra:',err)
        alert('Resgatar falhou. Tente novamente.')
      }finally{
        this.isLoading=false
      }
    },

    openExtraSurpriseModal(){
      this.selectedSurpriseOption=null
      this.extraSurpriseDialog=true
    },
    cancelExtraSurprise(){
      this.extraSurpriseDialog=false
      this.selectedSurpriseOption=null
    },
    async confirmExtraSurpriseChoice(){
      if(this.isLoading)return
      this.isLoading=true
      try{
        await redeemExtraSurprise(auth.token,this.selectedSurpriseOption)
        alert(this.t('escala.surpriseGMClick')+` (item ${this.selectedSurpriseOption})!`)
        this.extraEligible=false
      }catch(err){
        console.error('Erro ao resgatar surpresa extra:',err)
        alert('Resgatar falhou. Tente novamente.')
      }finally{
        this.isLoading=false
        this.cancelExtraSurprise()
      }
    },

    async loadSpentPoints(){
      try{
        const {spent}=await fetchScaleSpentPoints(auth.token)
        this.spentPoints=spent
      }catch(err){console.error(err)}
    },
    async loadRedeemedTiers(){
      try{
        const {rescues}=await fetchScaleRescueStatus(auth.token)
        this.redeemedTiers=rescues
      }catch(err){console.error(err)}
    },
    async loadRankList(){
      try{
        const {ranking}=await fetchScaleRanking(auth.token)
        this.rankList=ranking
      }catch(err){console.error(err)}
    },
    updateTimeRemaining(){
      const now=new Date()
      const end=new Date(now.getFullYear(),now.getMonth()+1,0,23,59,59)
      const diff=end-now
      if(diff<=0){this.timeRemaining='0d 0h 0m 0s';clearInterval(this.timer);return}
      const d=Math.floor(diff/86400000),
            h=Math.floor((diff%86400000)/3600000),
            m=Math.floor((diff%3600000)/60000),
            s=Math.floor((diff%60000)/1000)
      this.timeRemaining=`${d}d ${h}h ${m}m ${s}s`
    },
    async loadIni(path){
      const res=await fetch(path)
      const buf=await res.arrayBuffer()
      const txt=new TextDecoder('latin1').decode(buf)
      return txt.split('\n')
        .map(l=>l.trim())
        .filter(l=>l&&!l.startsWith(';'))
        .map(l=>l.split('|'))
        .filter(p=>p.length>1)
        .map(([id,value])=>({id:Number(id),value}))
    },
    async loadItemData(){
      try{
        const [c1,c2,t1,t2]=await Promise.all([
          this.loadIni('/auxiliadores/C_Item.ini'),
          this.loadIni('/auxiliadores/C_ItemMall.ini'),
          this.loadIni('/auxiliadores/T_Item.ini'),
          this.loadIni('/auxiliadores/T_ItemMall.ini')
        ])
        const textures=[...c1,...c2],names=[...t1,...t2]
        textures.forEach(e=>this.textureMap[e.id]=e.value)
        names.forEach(e=>this.nameMap[e.id]=e.value)
        for(const tier of this.tiers){
          const tex=this.textureMap[tier.imageItemId]
          if(!tex)continue
          const url=`/auxiliadores/itemicon/${tex}.png`
          tier.image=await new Promise((res,rej)=>
            new THREE.TextureLoader().load(url,tx=>res(tx.image.currentSrc),undefined,rej)
          )
        }
      }catch(err){console.error(err)}
    },
    async loadExtraStatus(){
      try{
        const {eligible,rank}=await fetchExtraStatus(auth.token)
        this.extraEligible=eligible
        this.extraRank=rank
      }catch(err){console.error(err)}
    }
  },
  mounted(){
    this.loadSpentPoints()
    this.loadRedeemedTiers()
    this.loadItemData()
    this.loadRankList()
    this.updateTimeRemaining()
    this.timer=setInterval(this.updateTimeRemaining,1000)
    this.loadExtraStatus()
  },
  unmounted(){
    clearInterval(this.timer)
  }
})
</script>

<style scoped>
.full-screen-background {
  background-image: url('@/assets/background/fundotrocasenha.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  min-height: 100vh;
  width: 100%;
  position: relative;
}
.escala-wrapper {
  max-width: 1000px;
  margin: 0 auto;
  padding: 20px;
  color: #fff;
  border-radius: 6px;
  background-color: rgba(0, 0, 0, 0.459);
}
.spent { font-size: 1rem; }
.tiers-row { flex-wrap: wrap; }
.tier-card {
  cursor: pointer;
  background-color: rgba(0, 0, 0, 0.459);
  transition: background 0.3s;
}
.tier-card.reached { background: #5555553d; }
.tier-card.redeemed { background: #35850d5d; }
.tier-header { display: flex; align-items: center; justify-content: center; }
.tier-title { font-weight: 600; }
.tier-image { width: 48px; height: 48px; margin: 0 auto; }
.tier-value { font-size: 0.9rem; margin-top: 4px; }
.grid-title { font-size: 1.1rem; font-weight: 500; }
.item-card {
  background-color: rgba(0, 0, 0, 0.459);
  border: 1px solid #444;
  border-radius: 6px;
  padding: 10px;
  width: 100%;
}
.item-image { width: 50px; height: 50px; margin: 0 auto; }
.card-name { font-size: 1.06rem; text-align: left; }
.inline-icon { display: inline-block; vertical-align: middle; width: 30px; height: 30px; margin-right: 4px; }
.bundle-list { list-style: none; padding-left: 1rem; margin: 0; }
.bundle-list li { margin-bottom: 0.25rem; }
.ranking-wrapper {
  background-color: rgba(0, 0, 0, 0.459);
  padding: 20px;
  border-radius: 6px;
  color: #fff;
  margin-bottom: 16px;
}
.recompensar-wrapper {
  background-color: rgba(0, 0, 0, 0.459);
  padding: 16px;
  border-radius: 6px;
  color: #fff;
}
.extra-item { margin-bottom: 16px; }
.reward-image { width: 32px; height: 32px; }
.instruction { margin-top: 16px; font-size: 0.95rem; font-weight: 500; }
.v-dialog .inline-icon { vertical-align: middle; margin-right: 4px; }
.v-btn.orange.darken-2 { color: #fff !important; }
</style>
